// Base de conocimiento (objeto articulo de Twenty) y su buscador en correo-api (conocimiento.mjs).
import { useQuery } from '@tanstack/react-query';

import { request } from './http';
import { create, listAll, remove, update } from './rest';

export type Articulo = {
  id: string;
  titulo: string;
  categoria: string;
  cuerpo: string;
  resumen: string;
  etiquetas: string[];
  publicado: boolean;
  vistas: number;
  utiles: number;
  noUtiles: number;
  zammadId: number | null;
  actualizado: string | null;
  actualizadoPor: string | null;
};

type Raw = {
  id: string;
  name: string | null;
  categoria: string | null;
  cuerpo: string | null;
  resumen?: string | null;
  etiquetas?: string[] | null;
  publicado: boolean | null;
  vistas?: number | null;
  utiles?: number | null;
  noUtiles?: number | null;
  zammadId?: number | null;
  updatedAt?: string | null;
  updatedBy?: { name?: string | null; source?: string | null } | null;
};

export const SIN_CATEGORIA = 'Sin categoría';

export const deRaw = (a: Raw): Articulo => ({
  id: a.id,
  titulo: a.name ?? '',
  categoria: (a.categoria ?? '').trim() || SIN_CATEGORIA,
  cuerpo: a.cuerpo ?? '',
  resumen: a.resumen ?? '',
  etiquetas: Array.isArray(a.etiquetas) ? a.etiquetas.filter(Boolean) : [],
  publicado: !!a.publicado,
  vistas: Number(a.vistas) || 0,
  utiles: Number(a.utiles) || 0,
  noUtiles: Number(a.noUtiles) || 0,
  zammadId: a.zammadId ?? null,
  actualizado: a.updatedAt ?? null,
  // "System" o la llave de API no son una persona: no se muestran como autor.
  actualizadoPor: a.updatedBy?.name && !/^(system|sistema)$/i.test(a.updatedBy.name) ? a.updatedBy.name : null,
});

export const articulos = async (): Promise<Articulo[]> => (await listAll<Raw>('articulos', { orderBy: 'name[AscNullsLast]' }, 3000)).map(deRaw);

export const useArticulos = () => useQuery({ queryKey: ['articulos'], queryFn: articulos, staleTime: 5 * 60_000 });

export type DatosArticulo = { titulo: string; categoria: string; cuerpo: string; resumen: string; etiquetas: string[] };

export async function guardarArticulo(id: string | null, d: DatosArticulo): Promise<string> {
  const datos = { name: d.titulo.trim(), categoria: d.categoria.trim() || SIN_CATEGORIA, cuerpo: d.cuerpo, resumen: d.resumen.trim() || null, etiquetas: d.etiquetas.length ? d.etiquetas : null };
  const r = id ? await update<{ id: string }>('articulos', id, datos) : await create<{ id: string }>('articulos', datos);
  return r?.id ?? id ?? '';
}

// Borrado suave de Twenty: el articulo pasa a su papelera (se puede restaurar desde Twenty).
export const borrarArticulo = (id: string) => remove('articulos', id);

// ---------- Buscador del servidor ----------

export type Resultado = { id: string; titulo: string; categoria: string; resumen: string | null; fragmento: string | null; vistas: number };
export type Busqueda = { articulos: Resultado[]; total: number; aproximado: boolean };

// null si el buscador no responde: quien llama usa la busqueda en el navegador.
export async function buscarEnServidor(q: string, categoria = '', signal?: AbortSignal): Promise<Busqueda | null> {
  try {
    return await request<Busqueda>('/correo/api/conocimiento/buscar', { query: { q: q.trim(), categoria }, signal });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    return null;
  }
}

export const sumarVista = (id: string) => request<{ vistas: number }>(`/correo/api/conocimiento/${id}/vista`, { method: 'POST', body: {} });
export const marcarUtil = (id: string, util: boolean) => request<{ utiles: number; noUtiles: number }>(`/correo/api/conocimiento/${id}/util`, { method: 'POST', body: { util } });

// Sugerencias para un caso (titulo + etiquetas): basta una palabra. [] si el buscador no responde.
export async function buscarSugeridos(texto: string, signal?: AbortSignal): Promise<Resultado[]> {
  try {
    return (await request<Busqueda>('/correo/api/conocimiento/buscar', { query: { q: texto.trim().slice(0, 200), modo: 'cualquiera', limite: 3 }, signal })).articulos ?? [];
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    return [];
  }
}
